/*
 * dm-global.js
 * Shared helpers, the one modal/drawer manager (focus trap, Escape, focus restore, scroll lock),
 * shop dropdown, sticky header state and image fallback. Vanilla JS, no dependencies.
 */
(function () {
  'use strict';

  var doc = document;
  var DM = (window.DM = window.DM || {});

  var cfg = {};
  try {
    cfg = JSON.parse(doc.getElementById('dm-config').textContent) || {};
  } catch (e) {
    cfg = {};
  }
  cfg.routes = cfg.routes || {};
  cfg.shipping = cfg.shipping || { threshold: 12000, standard: 899 };
  cfg.strings = cfg.strings || {};
  /* The Liquid "t" filter HTML-escapes its output (apostrophes become &#39;). Decode once so
     textContent shows the real character and DM.escape does not double-escape. */
  Object.keys(cfg.strings).forEach(function (key) {
    if (typeof cfg.strings[key] !== 'string') return;
    cfg.strings[key] = cfg.strings[key].replace(/&(?:amp|lt|gt|quot|#39);/g, function (entity) {
      return { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" }[entity];
    });
  });
  cfg.upsell = cfg.upsell || {};
  DM.config = cfg;
  DM.strings = cfg.strings;

  /* ---------- small helpers ---------- */
  DM.$ = function (sel, root) {
    return (root || doc).querySelector(sel);
  };
  DM.$$ = function (sel, root) {
    return Array.prototype.slice.call((root || doc).querySelectorAll(sel));
  };
  DM.escape = function (value) {
    return String(value === undefined || value === null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };
  /* Replace {token} placeholders in a string with values. */
  DM.fmt = function (template, values) {
    return String(template || '').replace(/\{(\w+)\}|__([A-Z]+)__/g, function (m, k1, k2) {
      var key = k1 || k2.toLowerCase();
      return values && values[key] !== undefined ? values[key] : m;
    });
  };
  DM.money = function (cents) {
    if (window.DMShipping) return window.DMShipping.formatMoney(cents, cfg.currency, cfg.locale);
    return '$' + (Math.round(cents) / 100).toFixed(2);
  };
  DM.reducedMotion = function () {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  };
  DM.sized = function (url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  };

  /* Polite screen reader announcement. */
  var liveTimer = null;
  DM.announce = function (message) {
    var live = doc.getElementById('dm-live');
    if (!live || !message) return;
    live.textContent = '';
    window.clearTimeout(liveTimer);
    liveTimer = window.setTimeout(function () {
      live.textContent = message;
    }, 60);
  };

  /* ---------- modal and drawer manager ---------- */
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  var stack = [];
  var CLOSE_MS = 240;

  function visible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }
  function focusables(root) {
    return DM.$$(FOCUSABLE, root).filter(visible);
  }
  function panelOf(modal) {
    return modal.querySelector('.dm-drawer__panel, .dm-modal__panel') || modal;
  }
  function scrollbarWidth() {
    return window.innerWidth - doc.documentElement.clientWidth;
  }

  DM.modal = {
    isOpen: function (modal) {
      return stack.indexOf(modal) > -1;
    },
    open: function (modal, opener) {
      if (!modal || stack.indexOf(modal) > -1) return;
      // No stacked popups: close anything else immediately.
      stack.slice().forEach(function (other) {
        DM.modal.close(other, { instant: true, restoreFocus: false });
      });
      modal.__dmOpener = opener || doc.activeElement;
      window.clearTimeout(modal.__dmTimer);
      modal.hidden = false;
      var sbw = scrollbarWidth();
      doc.documentElement.style.setProperty('--dm-scrollbar', sbw + 'px');
      doc.documentElement.classList.add('dm-modal-open');
      stack.push(modal);
      // Force a frame so the slide-in transition runs.
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          modal.classList.add('is-open');
        });
      });
      var target = modal.querySelector('[data-dm-autofocus]') || panelOf(modal);
      window.setTimeout(function () {
        if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
      }, 30);
      modal.dispatchEvent(new CustomEvent('dm:modal:open', { bubbles: true }));
      doc.dispatchEvent(new CustomEvent('dm:modal:change', { detail: { open: true } }));
    },
    close: function (modal, options) {
      options = options || {};
      var index = stack.indexOf(modal);
      if (index === -1) return;
      stack.splice(index, 1);
      modal.classList.remove('is-open');
      var finish = function () {
        modal.hidden = true;
      };
      if (options.instant || DM.reducedMotion()) {
        finish();
      } else {
        modal.__dmTimer = window.setTimeout(finish, CLOSE_MS);
      }
      if (!stack.length) {
        doc.documentElement.classList.remove('dm-modal-open');
        doc.documentElement.style.removeProperty('--dm-scrollbar');
      }
      var opener = modal.__dmOpener;
      modal.__dmOpener = null;
      if (options.restoreFocus !== false && opener && doc.contains(opener) && typeof opener.focus === 'function') {
        window.setTimeout(function () {
          opener.focus({ preventScroll: true });
        }, 10);
      }
      modal.dispatchEvent(new CustomEvent('dm:modal:close', { bubbles: true }));
      doc.dispatchEvent(new CustomEvent('dm:modal:change', { detail: { open: stack.length > 0 } }));
    },
    top: function () {
      return stack[stack.length - 1] || null;
    }
  };

  doc.addEventListener('keydown', function (event) {
    var top = DM.modal.top();
    if (!top) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      DM.modal.close(top);
      return;
    }
    if (event.key !== 'Tab') return;
    var panel = panelOf(top);
    var items = focusables(panel);
    if (!items.length) {
      event.preventDefault();
      panel.focus();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = doc.activeElement;
    if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Keep focus inside the open modal.
  doc.addEventListener('focusin', function (event) {
    var top = DM.modal.top();
    if (top && !top.contains(event.target)) {
      var panel = panelOf(top);
      if (panel && typeof panel.focus === 'function') panel.focus({ preventScroll: true });
    }
  });

  /* ---------- delegated clicks ---------- */
  doc.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var opener = target.closest('[data-dm-open]');
    if (opener) {
      var modal = doc.getElementById(opener.getAttribute('data-dm-open'));
      if (modal) {
        event.preventDefault();
        DM.modal.open(modal, opener);
      }
      return;
    }

    var closer = target.closest('[data-dm-close]');
    if (closer) {
      var host = closer.closest('[data-dm-modal]');
      if (host) {
        // A link inside a drawer (for example Find your fit) should still navigate.
        if (!(closer.tagName === 'A' && closer.getAttribute('href'))) event.preventDefault();
        DM.modal.close(host);
      }
      return;
    }

    var shopToggle = target.closest('[data-dm-shop-toggle]');
    if (shopToggle) {
      toggleShop(shopToggle.closest('[data-dm-shop]'));
      return;
    }
    closeShopMenus(target);
  });

  /* ---------- shop dropdown ---------- */
  function setShop(root, open, returnFocus) {
    if (!root) return;
    var toggle = root.querySelector('[data-dm-shop-toggle]');
    var panel = root.querySelector('.dm-shop-menu');
    if (!toggle || !panel) return;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.hidden = !open;
    root.classList.toggle('is-open', open);
    if (!open && returnFocus) toggle.focus();
  }
  function toggleShop(root) {
    if (!root) return;
    var toggle = root.querySelector('[data-dm-shop-toggle]');
    setShop(root, toggle.getAttribute('aria-expanded') !== 'true');
  }
  function closeShopMenus(from) {
    DM.$$('[data-dm-shop].is-open').forEach(function (root) {
      if (!from || !root.contains(from)) setShop(root, false);
    });
  }
  doc.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' || DM.modal.top()) return;
    var open = DM.$('[data-dm-shop].is-open');
    if (open) {
      event.preventDefault();
      setShop(open, false, true);
    }
  });
  doc.addEventListener('focusin', function (event) {
    closeShopMenus(event.target);
  });

  /* ---------- sticky header ---------- */
  function initHeader() {
    var header = DM.$('[data-dm-header]');
    if (!header || header.__dmReady) return;
    header.__dmReady = true;
    var wrap = header.closest('.shopify-section');
    if (wrap) wrap.classList.add('dm-header-wrap');
    var ticking = false;
    function update() {
      ticking = false;
      var top = (wrap || header).getBoundingClientRect().top;
      header.classList.toggle('is-stuck', top <= 0 && window.scrollY > 0);
    }
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }
  initHeader();
  doc.addEventListener('shopify:section:load', initHeader);

  /* ---------- image failure fallback ---------- */
  doc.addEventListener(
    'error',
    function (event) {
      var el = event.target;
      if (el && el.tagName === 'IMG' && el.classList.contains('dm-img')) {
        el.classList.add('dm-img--failed');
      }
    },
    true
  );
})();
